import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeHighlight from "rehype-highlight";
import "highlight.js/styles/github-dark.css";
import "../styles/docs.css";

const DOC_SECTIONS = [
  {
    title: "Overview",
    files: [
      "README.md",
      "overview/COMPONENTS.md",
      "overview/STATE-MANAGEMENT.md",
    ],
  },
  {
    title: "Flows",
    files: [
      "flows/AUTH-FLOW.md",
      "flows/API-FLOW.md",
      "flows/ROUTING-FLOW.md",
      "flows/ERROR-HANDLING.md",
    ],
  },
  {
    title: "Pages",
    files: [
      "pages/PAGES-OVERVIEW.md",
      "pages/PAGE-FLOWS.md",
      "pages/DASHBOARD.md",
      "pages/LOGIN.md",
      "pages/LANDLORD.md",
      "pages/LANDLORD-METRICS.md",
      "pages/TENANTS.md",
      "pages/TENANT-METRICS.md",
      "pages/REPORTS.md",
      "pages/ASSIGNED-PROPERTIES.md",
      "pages/RENTED-PROPERTIES.md",
      "pages/FORENSIC-ANALYSIS.md",
      "pages/11LABS-CONVERSATIONS.md",
      "pages/CREDITS-LEDGER.md",
      "pages/ANALYSIS-LEDGER.md",
      "pages/LLM-COST-ANALYSIS.md",
    ],
  },
];

function fileLabel(filePath) {
  const part = filePath.split("/").pop() || filePath;
  return part.replace(/\.md$/i, "").replace(/-/g, " ");
}

function resolveDocHref(href, currentFile) {
  if (!href) return null;
  if (href.startsWith("#")) return href;
  if (/^(https?:)?\/\//i.test(href) || href.startsWith("mailto:")) return href;

  const cleanHref = href.split("#")[0].split("?")[0];
  if (!cleanHref.toLowerCase().endsWith(".md")) return href;

  const normalizedHref = cleanHref.replace(/^\/+/, "");
  const currentDir =
    currentFile.lastIndexOf("/") >= 0
      ? currentFile.slice(0, currentFile.lastIndexOf("/") + 1)
      : "";

  const stack = (currentDir + normalizedHref).split("/");
  const resolved = [];

  stack.forEach((part) => {
    if (!part || part === ".") return;
    if (part === "..") {
      resolved.pop();
      return;
    }
    resolved.push(part);
  });

  return `/docs?file=${encodeURIComponent(resolved.join("/"))}`;
}

export default function Docs() {
  const location = useLocation();
  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const allFiles = useMemo(
    () => DOC_SECTIONS.flatMap((section) => section.files),
    []
  );

  const selectedFile = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const file = params.get("file");
    if (!file || !allFiles.includes(file)) {
      return "README.md";
    }
    return file;
  }, [allFiles, location.search]);

  useEffect(() => {
    let isActive = true;

    async function loadMarkdown() {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(`/_docs/${selectedFile}`);
        if (!response.ok) {
          throw new Error(`Could not load ${selectedFile}`);
        }
        const text = await response.text();
        if (isActive) {
          setMarkdown(text);
        }
      } catch (loadError) {
        if (isActive) {
          setMarkdown("");
          setError(loadError.message || "Failed to load document");
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    }

    loadMarkdown();

    return () => {
      isActive = false;
    };
  }, [selectedFile]);

  return (
    <div className="docs-page">
      <aside className="docs-sidebar">
        <h2 className="docs-sidebar-title">Documentation</h2>
        {DOC_SECTIONS.map((section) => (
          <div key={section.title} className="docs-section">
            <h3>{section.title}</h3>
            <ul>
              {section.files.map((filePath) => {
                const isActive = filePath === selectedFile;
                return (
                  <li key={filePath}>
                    <Link
                      to={`/docs?file=${encodeURIComponent(filePath)}`}
                      className={isActive ? "active" : ""}
                    >
                      {fileLabel(filePath)}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </aside>

      <section className="docs-content">
        {loading ? <p className="docs-status">Loading docs...</p> : null}
        {error ? <p className="docs-status docs-error">{error}</p> : null}
        {!loading && !error ? (
          <article className="docs-markdown">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw, rehypeHighlight]}
              components={{
                a({ href, children, ...props }) {
                  const resolvedHref = resolveDocHref(href, selectedFile);
                  const isExternal =
                    typeof resolvedHref === "string" &&
                    /^(https?:)?\/\//i.test(resolvedHref);

                  return (
                    <a
                      href={resolvedHref || href}
                      target={isExternal ? "_blank" : undefined}
                      rel={isExternal ? "noreferrer" : undefined}
                      {...props}
                    >
                      {children}
                    </a>
                  );
                },
                table({ children }) {
                  return (
                    <div className="docs-table-wrap">
                      <table>{children}</table>
                    </div>
                  );
                },
              }}
            >
              {markdown}
            </ReactMarkdown>
          </article>
        ) : null}
      </section>
    </div>
  );
}
